<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import { useDebounceFn, useDevicePixelRatio, useEventListener, useResizeObserver } from '@vueuse/core';
  import { FillRenderer, useSheetFit } from '@/features/FormBuilder';
  import { readMatrixReturnFolder } from '@/shared/lib';
  import { ColoredButton, SimpleLoading } from '@/shared/ui';
  import { useFillLogsReading } from '../model/useFillLogsReading';
  import { useSheetZoom } from '../model/useSheetZoom';
  import { LOGS_SIDEBAR_GAP, logsScrollGutterStyle, measureScrollbarWidth } from '../model/logs-scroll-gutter';
  import { FILL_LOGS_LOAD_ERROR, useFillLogsPage } from '../model/useFillLogsPage';
  import FillLogsHeader from './FillLogsHeader.vue';
  import FillLogsPdfView from './FillLogsPdfView.vue';
  import FillLogsSidebar from './FillLogsSidebar.vue';
  import FillLogsTextView from './FillLogsTextView.vue';
  import FillLogsZoom from './FillLogsZoom.vue';

  const route = useRoute();
  // The router already decodes the params; decoding again would break a literal "%".
  const prefix = computed(() => String(route.params.prefix ?? ''));
  const recordId = computed(() => String(route.params.recordId ?? ''));

  const {
    loading,
    error,
    title,
    recordLabel,
    timeline,
    total,
    hasErpEntry,
    current,
    isLatest,
    selectedNumber,
    showHighlights,
    compiled,
    changeMarks,
    correctedLegend,
    correctedWord,
    load,
    select,
    selectLatest,
    selectNewer,
    selectOlder,
  } = useFillLogsPage(prefix, recordId, readMatrixReturnFolder(route.query));

  const scrollRef = ref<HTMLElement | null>(null);
  const orientation = computed(() => compiled.value?.orientation ?? 'portrait');
  const { sheetWidth, fitScale } = useSheetFit(scrollRef, orientation, { widthRatio: 1 });
  const { zoomPercent, zoom, canZoomIn, canZoomOut, zoomIn, zoomOut, onWheel } = useSheetZoom();
  const paperScale = computed(() => fitScale.value * zoom.value);
  const displayWidth = computed(() => Math.round(sheetWidth.value * fitScale.value));
  const paperWidth = computed(() => Math.round(sheetWidth.value * paperScale.value));

  const { view, showAll, paperRef, text, showOnPaper } = useFillLogsReading(compiled, current, timeline, hasErpEntry);

  // A browser zoom changes the scrollbar width in CSS pixels. Chrome reports it through the device
  // pixel ratio; Safari keeps the ratio and only fires resize.
  const scrollbarWidth = ref(measureScrollbarWidth());
  const remeasureScrollbar = () => {
    scrollbarWidth.value = measureScrollbarWidth();
  };
  const { pixelRatio } = useDevicePixelRatio();
  watch(pixelRatio, remeasureScrollbar);
  useEventListener(window, 'resize', useDebounceFn(remeasureScrollbar, 150));
  const scrollGutterStyle = computed(() => logsScrollGutterStyle(scrollbarWidth.value));

  // offsetHeight ignores the CSS transform, so the scale never feeds back into the measurement.
  const paperHeight = ref(0);
  useResizeObserver(paperRef, () => {
    paperHeight.value = paperRef.value?.offsetHeight ?? 0;
  });
  const pdfResponse = computed(() => (current.value?.kind === 'pdf' ? current.value : null));
  const showsPaper = computed(() => view.value === 'form' && !pdfResponse.value);

  function onPaperWheel(event: WheelEvent): void {
    if (showsPaper.value) onWheel(event);
  }
</script>

<template>
  <div
    class="flex h-full min-h-0 w-full"
    data-testid="fill-logs-page"
  >
    <div
      v-if="loading"
      class="flex flex-1 items-center justify-center px-6"
      data-testid="fill-logs-loading"
    >
      <SimpleLoading />
    </div>

    <div
      v-else-if="error"
      class="flex flex-1 flex-col items-center gap-4 px-6 py-8 text-center"
      data-testid="fill-logs-error"
    >
      <p class="text-lg text-gray-07">
        {{ FILL_LOGS_LOAD_ERROR }}
      </p>
      <ColoredButton
        text="Tentar novamente"
        data-testid="fill-logs-retry"
        @click="load"
      />
    </div>

    <div
      v-else-if="compiled"
      class="flex min-h-0 min-w-0 flex-1 pr-6"
      :style="{ gap: `${LOGS_SIDEBAR_GAP}px` }"
    >
      <section class="flex min-h-0 min-w-0 flex-1 flex-col">
        <FillLogsHeader
          :title="title"
          :record-label="recordLabel"
          :current="current"
          :total="total"
          :is-latest="isLatest"
          :view="view"
          @update:view="view = $event"
          @latest="selectLatest"
        >
          <template #tools>
            <FillLogsZoom
              v-if="current && showsPaper"
              :percent="zoomPercent"
              :can-zoom-in="canZoomIn"
              :can-zoom-out="canZoomOut"
              @zoom-in="zoomIn"
              @zoom-out="zoomOut"
            />
          </template>
        </FillLogsHeader>

        <!-- Without relative, the sr-only spans of a long list escape this scroll and stretch the layout, so the whole page scrolls into a blank screen. -->
        <div
          ref="scrollRef"
          class="relative min-h-0 flex-1 overflow-auto [scrollbar-gutter:stable] pt-1 pb-6 pl-6"
          :style="scrollGutterStyle"
          data-testid="fill-logs-scroll"
          @wheel="onPaperWheel"
        >
          <FillLogsPdfView
            v-if="view === 'form' && pdfResponse"
            :record-id="Number(recordId)"
            :response-number="pdfResponse.number"
            :file-name="pdfResponse.fileName"
          />
          <div
            v-else-if="view === 'form'"
            class="mx-auto"
            :style="{ width: `${paperWidth}px`, height: `${paperHeight * paperScale}px` }"
          >
            <section
              ref="paperRef"
              class="light-surface rounded-md bg-white-01 p-8 text-gray-07 shadow-md"
              :style="{ width: `${sheetWidth}px`, transform: `scale(${paperScale})`, transformOrigin: 'top left' }"
              data-testid="fill-logs-paper"
            >
              <FillRenderer
                :form="compiled"
                read-only
                :change-marks="changeMarks"
                :show-highlights="showHighlights"
              />
            </section>
          </div>
          <p
            v-else-if="pdfResponse"
            class="mx-auto rounded-md border border-gray-02 bg-white-01 px-6 py-4 text-sm/[22px] text-gray-03"
            :style="{ maxWidth: `${displayWidth}px` }"
            data-testid="fill-logs-pdf-changes"
          >
            Esta resposta é um PDF enviado.
          </p>
          <div
            v-else-if="text && current"
            class="mx-auto"
            :style="{ maxWidth: `${displayWidth}px` }"
          >
            <FillLogsTextView
              :text="text"
              :current="current"
              :show-all="showAll"
              :corrected-word="correctedWord"
              @update:show-all="showAll = $event"
              @show="showOnPaper"
            />
          </div>
        </div>
      </section>

      <div class="flex min-h-0 shrink-0 flex-col pt-4 pb-6">
        <FillLogsSidebar
          :timeline="timeline"
          :selected-number="selectedNumber"
          :show-highlights="showHighlights"
          :corrected-legend="correctedLegend"
          @select="select"
          @newer="selectNewer"
          @older="selectOlder"
          @update:show-highlights="showHighlights = $event"
        />
      </div>
    </div>
  </div>
</template>
